import React, { useState, useEffect } from "react";
import MonthlyWeekHead from "./MonthlyWeekHead";
import { IScheduleEvent } from "../../redux/reducers/schedule.reducer";
import { useMemo } from "react";
import moment from "moment";
import Month from "./Month";
import { useSelector } from "react-redux";
import { storeStateType } from "../../redux/store";
import { Box } from "@mui/material";
import Loading from "react-loading";
import {
  IFacilityOption,
  eventForm,
} from "../CalendarForms/Types/calendarTypes";
import { filterEventsType } from "../../redux/actions/schedule.actions";

export type DatesType = {
  date: string;
  fullDate: string;
  isCurrentMonth: boolean;
  isScheduleAddable: boolean;
  isCurrentDate: boolean;
};
export interface ColorCodeType {
  [key: number]: string;
}

type MonthlyCalendarPropsType = {
  overallSelectedDate: string;
  selectedFacility: null | IFacilityOption;
  setShowingEventForm: React.Dispatch<React.SetStateAction<eventForm>>;
  allEvents: IScheduleEvent[];
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedEvent: React.Dispatch<React.SetStateAction<IScheduleEvent | null>>;
  filterByWorktype: string[];
  setFilterEvents: React.Dispatch<React.SetStateAction<filterEventsType>>;
  setShowAssignEmployeeModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function MonthlyCalendar(props: MonthlyCalendarPropsType) {
  const {
    overallSelectedDate,
    selectedFacility,
    setShowingEventForm,
    allEvents,
    setSelectedEvent,
    setShowEditModal,
    setFilterEvents,
    setShowAssignEmployeeModal,
  } = props;
  const { loading } = useSelector((state: storeStateType) => state.schedule);

  let themeMode = useSelector((state: storeStateType) => state.UI.themeMode);

  let weekDaysList: string[] = useMemo(
    () => [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    []
  );

  let colorCodes: ColorCodeType = useMemo(
    () => ({
      0: "#404040", //dark grey --closed
      1: " #006400", //green --open
      2: "#4169E1", //light blue --unassigned
      3: "#AA2C2C", //dark red --unfullfilled
      4: "#022e1f", //tomato red --cancelled
      5: " #191970", //royal blue --assigned
    }),
    []
  );

  const [dates, setDates] = useState<DatesType[]>([]);

  useEffect(() => {
    let dateObj = new Date(overallSelectedDate);

    let currentDateMoment = moment(moment(new Date()).format("YYYY-MM-DD"));

    dateObj.setDate(1);

    //to get start and end date for filter  - - - - - - - -
    let momtObj = moment(dateObj);

    // get the first date of the current month
    const firstDateOfMonth = momtObj.startOf("month").format("YYYY-MM-DD");

    // get the last date of the current month
    const lastDateOfMonth = momtObj.endOf("month").format("YYYY-MM-DD");
    if (firstDateOfMonth && lastDateOfMonth)
      setFilterEvents((filterEvent) => {
        return {
          ...filterEvent,
          start_date: firstDateOfMonth,
          end_date: lastDateOfMonth,
        };
      });
    //setting filter date ends here - - - - - - - - -

    let dateStartDay: number = dateObj.getDay();

    //all dates array
    let allDates: DatesType[] = [];

    //start from the first date moment obj and add initially with date
    let dateMomentObj = moment(dateObj);

    for (let row = 0; row < 6; row++) {
      for (let weekday = 0; weekday < 7; weekday++) {
        //checking if the month is going to start, rendering previous dates from Date 1 of current month
        if (dateStartDay > 0) {
          let momentObj = moment(dateObj);
          let calculatedDate = momentObj
            .subtract(dateStartDay, "days")
            .format("DD");
          allDates.push({
            date: calculatedDate,
            fullDate: momentObj.format("YYYY-MM-DD"),
            isCurrentMonth: false,
            isScheduleAddable: false,
            isCurrentDate: false,
          });

          dateStartDay = dateStartDay - 1;
        }

        //checking if  date is the start date of the calendar like if sunday is 1st date if
        //if not previous days will be rendered first
        else if (dateStartDay === 0) {
          //checking if date is in current month
          if (dateObj.getMonth() === dateMomentObj.month()) {
            // Compare the dates without time portion

            if (
              currentDateMoment.isBefore(
                moment(dateMomentObj.format("YYYY-MM-DD"))
              )
            ) {
              /**
               * the date to be pushed in the array is =>> render date
               * current date means today =>> current date
               */
              //render date is after current date
              allDates.push({
                date: dateMomentObj.format("DD"),
                fullDate: dateMomentObj.format("YYYY-MM-DD"),
                isCurrentMonth: true,
                isScheduleAddable: true,
                isCurrentDate: false,
              });
            } else if (
              currentDateMoment.isAfter(
                moment(dateMomentObj.format("YYYY-MM-DD"))
              )
            ) {
              //render date is before the current date
              allDates.push({
                date: dateMomentObj.format("DD"),
                fullDate: dateMomentObj.format("YYYY-MM-DD"),
                isCurrentMonth: true,
                isScheduleAddable: true,
                isCurrentDate: false,
              });
            } else {
              //current date is equal to render date
              allDates.push({
                date: dateMomentObj.format("DD"),
                fullDate: dateMomentObj.format("YYYY-MM-DD"),
                isCurrentMonth: true,
                isScheduleAddable: true,
                isCurrentDate: true,
              });
            }
          } else {
            //not in current month
            allDates.push({
              date: dateMomentObj.format("DD"),
              fullDate: dateMomentObj.format("YYYY-MM-DD"),
              isCurrentMonth: false,
              isScheduleAddable: false,
              isCurrentDate: false,
            });
          }

          dateMomentObj.add(1, "days");
        }
      }
    }

    setDates(allDates);
  }, [overallSelectedDate, setFilterEvents]);

  return (
    <div>
      <MonthlyWeekHead weekDays={weekDaysList} />
      {loading && (
        <Box
          height={"50vh"}
          width={"100%"}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Loading
            type="bars"
            color={themeMode === "dark" ? "#fcfcfc" : "#000000"}
            height={100}
            width={100}
          />
        </Box>
      )}
      {!loading && (
        <Month
          dates={dates}
          setShowingEventForm={setShowingEventForm}
          selectedFacility={selectedFacility}
          allEvents={allEvents}
          setSelectedEvent={setSelectedEvent}
          setShowEditModal={setShowEditModal}
          setShowAssignEmployeeModal={setShowAssignEmployeeModal}
          colorCodes={colorCodes}
        />
      )}
    </div>
  );
}
